import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge, Modal, Alert, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchContactsList, addContact, deleteContact, updateContact, fetchContactGroups } from "../services/api";
import Select from "react-select";

// ============================================
// UTILITAIRES
// ============================================
const formatPhoneNumber = (numero) => {
  if (!numero) return numero;
  if (numero.startsWith('241') && numero.length === 11) {
    return `+241 ${numero.substring(3, 5)} ${numero.substring(5, 8)} ${numero.substring(8)}`;
  }
  return numero;
};

const isValidInternationalPhone = (phone) => {
  if (!phone) return false;
  const phoneRegex = /^(241|\+241|\+242|\+243|\+237|\+221|\+225)?[0-9]{8,15}$/;
  const cleanPhone = phone.replace(/[\s-]/g, '');
  return phoneRegex.test(cleanPhone);
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'error': return 'bi-exclamation-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'info': return 'bi-info-circle-fill';
    default: return 'bi-bell-fill';
  }
};

const getNotificationBg = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'primary';
  }
};

const getAlertIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle';
    case 'danger': return 'bi-exclamation-triangle';
    case 'warning': return 'bi-exclamation-triangle';
    case 'info': return 'bi-info-circle';
    default: return 'bi-bell';
  }
};

export default function ContactManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const [newContact, setNewContact] = useState({
    nom: '', prenom: '', telephone: '', email: '',
    entreprise: '', fonction: '', groupes: []
  });

  const [selectedContact, setSelectedContact] = useState(null);
  const [editContact, setEditContact] = useState({
    nom: '', prenom: '', telephone: '', email: '',
    entreprise: '', fonction: '', groupes: []
  });

  const [contactToDelete, setContactToDelete] = useState(null);

  const initDoneRef = useRef(false);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    loadContacts();
    loadContactGroups();
  }, []);

  const loadContacts = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoadingContacts(true);
      setError("");

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Token d'authentification non trouvé");
        showAlert("Token d'authentification non trouvé.", 'danger');
        return;
      }

      const contactsData = await fetchContactsList(token);

      if (!Array.isArray(contactsData)) {
        setError("Format de données invalide reçu du serveur");
        return;
      }

      const formattedContacts = contactsData.map(contact => ({
        id: contact.id,
        entite_id: contact.entite_id,
        nom: contact.nom || '',
        prenom: contact.prenom || '',
        email: contact.email || '',
        telephone: contact.telephone || '',
        entreprise: contact.entreprise || '',
        fonction: contact.fonction || '',
        groupes: contact.groupes || [],
        date_ajout: contact.date_ajout || '',
        source_import: contact.source_import || '',
        actif: contact.actif,
        updated_at: contact.updated_at,
        created_at: contact.created_at
      }));

      setContacts(formattedContacts);

      if (showRefreshSpinner) {
        showNotification('Liste des contacts actualisée', 'success', 3000);
      }
    } catch (error) {
      console.error('❌ Erreur contacts:', error);
      const errorMessage = error.message || "Erreur lors du chargement";
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoadingContacts(false);
      setRefreshing(false);
    }
  };

  const loadContactGroups = async () => {
    try {
      setLoadingGroups(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const groupsData = await fetchContactGroups(token);
      if (Array.isArray(groupsData)) {
        setContactGroups(groupsData);
      }
    } catch (error) {
      console.error('❌ Erreur groupes:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  // ============================================
  // GROUPES
  // ============================================
  const getGroupNames = (groupIds) => {
    if (!groupIds) return ['Aucun groupe'];

    if (Array.isArray(groupIds)) {
      if (groupIds.length === 0) return ['Aucun groupe'];
      return groupIds.map(groupId => {
        const group = contactGroups.find(g => g.id === groupId);
        return group ? group.name : 'Groupe inconnu';
      });
    }

    if (typeof groupIds === 'string') {
      if (!groupIds.trim()) return ['Aucun groupe'];
      try {
        const parsed = JSON.parse(groupIds);
        if (Array.isArray(parsed)) {
          return parsed.map(groupId => {
            const group = contactGroups.find(g => g.id === groupId);
            return group ? group.name : 'Groupe inconnu';
          });
        }
      } catch {
        const group = contactGroups.find(g => g.id === groupIds);
        return group ? [group.name] : ['Groupe inconnu'];
      }
    }

    if (typeof groupIds === 'number') {
      const group = contactGroups.find(g => g.id === groupIds);
      return group ? [group.name] : ['Groupe inconnu'];
    }

    return ['Aucun groupe'];
  };

  const renderGroupBadges = (groupIds) => {
    const groupNames = getGroupNames(groupIds);

    const getColorFromName = (groupName) => {
      if (groupName === 'Aucun groupe' || groupName === 'Groupe inconnu') return 'gray';
      const colors = ['blue', 'green', 'red', 'amber', 'cyan', 'violet'];
      let hash = 0;
      for (let i = 0; i < groupName.length; i++) {
        hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    return (
      <div className="cm-group-badges">
        {groupNames.map((name, index) => (
          <span key={index} className={`cm-group-badge cm-group-badge-${getColorFromName(name)}`}>
            {name}
          </span>
        ))}
      </div>
    );
  };

  // ============================================
  // CRÉATION
  // ============================================
  const handleSaveContact = async () => {
    if (!newContact.nom.trim() || !newContact.prenom.trim() || !newContact.telephone.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    const cleanPhone = newContact.telephone.replace(/\s/g, '');
    if (!isValidInternationalPhone(cleanPhone)) {
      showAlert("Numéro de téléphone invalide", 'warning');
      return;
    }

    setCreateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token manquant');

      const contactData = {
        nom: newContact.nom.trim(),
        prenom: newContact.prenom.trim(),
        telephone: cleanPhone,
        email: newContact.email.trim() || '',
        entreprise: newContact.entreprise.trim() || '',
        fonction: newContact.fonction.trim() || '',
        groupes: newContact.groupes || []
      };

      await addContact(token, contactData);

      setNewContact({ nom: '', prenom: '', telephone: '', email: '', entreprise: '', fonction: '', groupes: [] });
      setShowAddContactModal(false);
      await loadContacts();
      showNotification(`Contact "${contactData.prenom} ${contactData.nom}" créé`, 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur création:', err);
      if (err.message.includes('409')) showAlert('Ce contact existe déjà', 'warning');
      else if (err.message.includes('401')) showAlert('Session expirée. Reconnectez-vous.', 'danger');
      else showAlert(`Erreur: ${err.message}`, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================
  // ÉDITION
  // ============================================
  const handleEditContact = (contact) => {
    setSelectedContact(contact);

    let groupesArray = [];
    if (contact.groupes) {
      if (Array.isArray(contact.groupes)) groupesArray = contact.groupes;
      else if (typeof contact.groupes === 'number') groupesArray = [contact.groupes];
      else if (typeof contact.groupes === 'string') {
        try {
          const parsed = JSON.parse(contact.groupes);
          groupesArray = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          groupesArray = [contact.groupes];
        }
      }
    }

    setEditContact({
      nom: contact.nom || '',
      prenom: contact.prenom || '',
      telephone: contact.telephone || '',
      email: contact.email || '',
      entreprise: contact.entreprise || '',
      fonction: contact.fonction || '',
      groupes: groupesArray
    });

    setShowEditContactModal(true);
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;

    if (!editContact.nom.trim() || !editContact.prenom.trim() || !editContact.telephone.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    const cleanPhone = editContact.telephone.replace(/\s/g, '');
    if (!isValidInternationalPhone(cleanPhone)) {
      showAlert("Numéro invalide", 'warning');
      return;
    }

    setUpdateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token manquant');

      const contactData = {
        nom: editContact.nom.trim(),
        prenom: editContact.prenom.trim(),
        telephone: cleanPhone,
        email: editContact.email.trim() || null,
        entreprise: editContact.entreprise.trim() || null,
        fonction: editContact.fonction.trim() || null,
        groupes: Array.isArray(editContact.groupes) ? editContact.groupes : []
      };

      await updateContact(token, selectedContact.id, contactData);

      setShowEditContactModal(false);
      setSelectedContact(null);
      await loadContacts();
      showNotification(`Contact "${contactData.prenom} ${contactData.nom}" mis à jour`, 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur mise à jour:', err);
      let errorMessage = `Erreur: ${err.message}`;
      if (err.message.includes('500')) errorMessage = 'Erreur serveur. Réessayez.';
      else if (err.message.includes('400')) errorMessage = 'Données invalides.';
      else if (err.message.includes('404')) errorMessage = 'Contact non trouvé.';
      showAlert(errorMessage, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  // ============================================
  // SUPPRESSION
  // ============================================
  const handleShowDetails = (contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;

    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token manquant');

      const response = await deleteContact(token, contactToDelete.id);

      if (response && (response.code === 200 || response.success)) {
        setContacts(prev => prev.filter(c => c.id !== contactToDelete.id));
        showNotification(`Contact "${contactToDelete.prenom} ${contactToDelete.nom}" supprimé`, 'success', 5000);
      } else {
        throw new Error(response?.message || 'Erreur suppression');
      }
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      if (err.message.includes('404')) {
        setContacts(prev => prev.filter(c => c.id !== contactToDelete.id));
        showNotification(`Contact supprimé`, 'success', 5000);
      } else if (err.message.includes('401')) {
        showAlert('Session expirée. Reconnectez-vous.', 'danger');
      } else {
        showAlert(`Erreur: ${err.message}`, 'error');
      }
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setContactToDelete(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedContacts.length === 0) return;

    setBulkDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token manquant');

      const deletePromises = selectedContacts.map(contactId => deleteContact(token, contactId));
      const results = await Promise.allSettled(deletePromises);

      const successfulIds = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' &&
            (result.value?.code === 200 || result.value?.success === true)) {
          successfulIds.push(selectedContacts[index]);
        }
      });

      if (successfulIds.length > 0) {
        setContacts(prev => prev.filter(c => !successfulIds.includes(c.id)));
        showNotification(`${successfulIds.length} contact(s) supprimé(s)`, 'success', 5000);
      }
      setSelectedContacts([]);
    } catch (err) {
      console.error('❌ Erreur suppression multiple:', err);
      showAlert(`Erreur: ${err.message}`, 'error');
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedContacts.length === 0) {
      showAlert("Sélectionnez au moins un contact", 'warning');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleAddContact = () => {
    setShowAddContactModal(true);
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = contacts.filter(item => {
      const searchTerm = filterText.toLowerCase();
      if (!searchTerm.trim()) return true;

      const matchesSearch =
        (item.nom && item.nom.toLowerCase().includes(searchTerm)) ||
        (item.prenom && item.prenom.toLowerCase().includes(searchTerm)) ||
        (item.telephone && item.telephone.includes(filterText)) ||
        (item.entreprise && item.entreprise.toLowerCase().includes(searchTerm)) ||
        (item.fonction && item.fonction.toLowerCase().includes(searchTerm));

      const matchesGroups = contactGroups.some(group => {
        const hasGroup = Array.isArray(item.groupes)
          ? item.groupes.includes(group.id)
          : item.groupes === group.id;
        return hasGroup && group.name.toLowerCase().includes(searchTerm);
      });

      return matchesSearch || matchesGroups;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [contacts, filterText, sortField, sortDirection, contactGroups]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = contacts.length;
    const actifs = contacts.filter(contact => contact.actif === 1).length;
    const inactifs = total - actifs;
    return { total, actifs, inactifs };
  }, [contacts]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedContacts(currentItems.map(contact => contact.id));
    else setSelectedContacts([]);
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedContacts.length === currentItems.length;
  const isSomeSelected = selectedContacts.length > 0 && selectedContacts.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedContacts.length > 0
      ? contacts.filter(contact => selectedContacts.includes(contact.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      showAlert("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      const enhancedData = dataToExport.map(contact => ({
        ...contact,
        statut: contact.actif === 1 ? "Actif" : "Inactif",
        numeroFormate: formatPhoneNumber(contact.telephone),
        groupe: getGroupNames(contact.groupes).join(', ')
      }));

      if (format === "csv") {
        const headers = ["Nom","Prénom","Numéro","Numéro Formaté","Email","Entreprise","Fonction","Groupe","Statut","Date Création"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...enhancedData.map(c => [
            c.nom, c.prenom, c.telephone, c.numeroFormate, c.email,
            c.entreprise, c.fonction, c.groupe, c.statut, c.created_at
          ].map(v => `"${v || ''}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-contacts-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(enhancedData.map(c => ({
          Nom: c.nom, Prénom: c.prenom, Numéro: c.telephone,
          "Numéro Formaté": c.numeroFormate, Email: c.email,
          Entreprise: c.entreprise, Fonction: c.fonction,
          Groupe: c.groupe, Statut: c.statut, "Date Création": c.created_at
        })));
        ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 18 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Contacts");
        XLSX.writeFile(wb, `export-contacts-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(enhancedData, null, 2), "application/json;charset=utf-8;", `export-contacts-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(enhancedData);
      }

      showNotification(`Export ${format.toUpperCase()} réussi`, 'success', 4000);
    } catch (error) {
      showAlert(`Erreur export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 2;
      let yPosition = margin;

      const primaryColor = [44, 62, 120];
      const softGray = [245, 246, 248];
      const darkText = [40, 40, 40];
      const borderGray = [210, 214, 220];

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("LISTE DES CONTACTS", pageWidth / 2, 12, { align: "center" });

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`Généré le ${new Date().toLocaleDateString("fr-FR")} • ${data.length} contact(s)`, pageWidth / 2, 20, { align: "center" });

      yPosition = 40;

      const headers = ["NOM", "PRÉNOM", "TÉLÉPHONE", "EMAIL", "ENTREPRISE", "FONCTION", "GROUPE"];
      const columnWidths = [25, 25, 28, 42, 32, 30, 25];
      const totalWidth = columnWidths.reduce((a, b) => a + b);

      const drawTableHeader = () => {
        doc.setFillColor(...primaryColor);
        doc.rect(margin, yPosition, totalWidth, 9, "F");
        let x = margin;
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, "bold");
        headers.forEach((head, i) => {
          doc.text(head, x + 2, yPosition + 6);
          x += columnWidths[i];
        });
        yPosition += 9;
      };

      drawTableHeader();
      doc.setFontSize(7);
      doc.setFont(undefined, "normal");

      data.forEach((row, index) => {
        if (yPosition > 265) {
          doc.addPage();
          yPosition = 20;
          drawTableHeader();
        }

        if (index % 2 === 0) {
          doc.setFillColor(...softGray);
          doc.rect(margin, yPosition, totalWidth, 7, "F");
        }

        let x = margin;
        doc.setTextColor(...darkText);

        const rowData = [
          row.nom || "-", row.prenom || "-", row.numeroFormate || row.telephone || "-",
          row.email || "-", row.entreprise || "-", row.fonction || "-", row.groupe || "-"
        ];

        rowData.forEach((cell, i) => {
          let text = cell.toString();
          const limit = [18, 18, 14, 28, 20, 18, 15][i];
          if (text.length > limit) text = text.substring(0, limit - 3) + "...";
          doc.text(text, x + 2, yPosition + 5);
          x += columnWidths[i];
        });

        yPosition += 7;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...borderGray);
        doc.line(margin, 283, pageWidth - margin, 283);
        doc.setFontSize(7);
        doc.setTextColor(110, 110, 110);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 289, { align: "center" });
      }

      doc.save(`contacts-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("❌ Erreur PDF:", error);
      throw new Error(`Erreur PDF: ${error.message}`);
    }
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="cm-sortable-header" onClick={() => handleSort(field)}>
      <span className="cm-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill cm-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="cm-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`cm-alert cm-alert-${alertMessage.type} mb-4`}>
          <i className={`bi ${getAlertIcon(alertMessage.type)}`}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="cm-alert-close" onClick={hideAlert}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="cm-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="cm-header-icon">
              <i className="bi bi-person-rolodex"></i>
            </div>
            <div>
              <h3 className="cm-header-title mb-1">Gestion des contacts</h3>
              <p className="cm-header-subtitle mb-0">
                Recherchez et gérez votre carnet d'adresses
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedContacts.length > 0 && (
              <Button className="cm-btn-danger" onClick={handleBulkDelete}>
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedContacts.length})</span>
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle className="cm-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat cm-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="cm-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button className="cm-btn-primary" onClick={handleAddContact}>
              <i className="bi bi-person-plus-fill"></i>
              <span>Ajouter un contact</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="cm-alert cm-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="cm-stat-card cm-stat-blue">
            <div className="cm-stat-icon-wrapper cm-stat-icon-blue">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="cm-stat-content">
              <div className="cm-stat-label">Total contacts</div>
              <div className="cm-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="cm-stat-card cm-stat-green">
            <div className="cm-stat-icon-wrapper cm-stat-icon-green">
              <i className="bi bi-person-check-fill"></i>
            </div>
            <div className="cm-stat-content">
              <div className="cm-stat-label">Contacts actifs</div>
              <div className="cm-stat-value">{stats.actifs.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="cm-stat-card cm-stat-gray">
            <div className="cm-stat-icon-wrapper cm-stat-icon-gray">
              <i className="bi bi-person-dash-fill"></i>
            </div>
            <div className="cm-stat-content">
              <div className="cm-stat-label">Contacts inactifs</div>
              <div className="cm-stat-value">{stats.inactifs.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="cm-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="cm-search-wrapper">
              <i className="bi bi-search cm-search-icon"></i>
              <input
                type="text"
                className="cm-search-input"
                placeholder="Rechercher par nom, prénom, numéro, entreprise..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="cm-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="cm-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6}>
            <button
              className="cm-btn-secondary w-100"
              onClick={() => loadContacts(true)}
              disabled={refreshing}
            >
              <i className={`bi ${refreshing ? 'bi-arrow-repeat cm-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="cm-table-card">
        {loadingContacts ? (
          <div className="cm-loading">
            <div className="cm-loading-spinner"></div>
            <div className="cm-loading-text">Chargement des contacts...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="cm-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="cm-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="cm-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="nom">
                      <i className="bi bi-person me-1"></i>Nom
                    </SortableHeader>
                    <SortableHeader field="prenom">
                      <i className="bi bi-person me-1"></i>Prénom
                    </SortableHeader>
                    <SortableHeader field="telephone">
                      <i className="bi bi-telephone me-1"></i>Numéro
                    </SortableHeader>
                    <SortableHeader field="entreprise">
                      <i className="bi bi-building me-1"></i>Entreprise
                    </SortableHeader>
                    <SortableHeader field="created_at">
                      <i className="bi bi-calendar me-1"></i>Créé le
                    </SortableHeader>
                    <SortableHeader field="fonction">
                      <i className="bi bi-briefcase me-1"></i>Fonction
                    </SortableHeader>
                    <th>
                      <i className="bi bi-collection me-1"></i>Groupes
                    </th>
                    <th>
                      <i className="bi bi-flag me-1"></i>Statut
                    </th>
                    <th className="text-end" style={{ width: '140px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((contact) => {
                      const isActif = contact.actif === 1;
                      return (
                        <tr key={contact.id} className="cm-row">
                          <td>
                            <div className="cm-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="cm-checkbox"
                                checked={selectedContacts.includes(contact.id)}
                                onChange={() => handleSelectContact(contact.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="cm-name-cell">
                              <div className="cm-avatar">
                                {contact.nom?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <span className="cm-name-main">{contact.nom || '—'}</span>
                            </div>
                          </td>
                          <td>
                            <span className="cm-prenom">{contact.prenom || '—'}</span>
                          </td>
                          <td>
                            <code className="cm-code">{contact.telephone || '—'}</code>
                          </td>
                          <td>
                            {contact.entreprise ? (
                              <span className="cm-badge cm-badge-secondary">
                                <i className="bi bi-building"></i>
                                {contact.entreprise}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className="cm-date-cell">
                              {contact.created_at ? new Date(contact.created_at).toLocaleDateString('fr-FR') : '—'}
                            </span>
                          </td>
                          <td>
                            <span className="cm-fonction">{contact.fonction || '—'}</span>
                          </td>
                          <td>
                            {renderGroupBadges(contact.groupes)}
                          </td>
                          <td>
                            <span className={`cm-status-badge ${isActif ? 'cm-status-active' : 'cm-status-inactive'}`}>
                              <i className={`bi ${isActif ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                              {isActif ? "Actif" : "Inactif"}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="cm-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                <button
                                  className="cm-action-btn cm-action-view"
                                  onClick={() => handleShowDetails(contact)}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                <button
                                  className="cm-action-btn cm-action-edit"
                                  onClick={() => handleEditContact(contact)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                <button
                                  className="cm-action-btn cm-action-delete"
                                  onClick={() => handleDeleteContact(contact)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">
                        <div className="cm-empty-state">
                          <div className="cm-empty-icon">
                            <i className="bi bi-person-x"></i>
                          </div>
                          <h5 className="cm-empty-title">Aucun contact trouvé</h5>
                          <p className="cm-empty-text">
                            {filterText
                              ? "Aucun contact ne correspond à votre recherche"
                              : "Commencez par ajouter votre premier contact"}
                          </p>
                          {filterText ? (
                            <button className="cm-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          ) : (
                            <button className="cm-btn-empty-reset" onClick={handleAddContact}>
                              <i className="bi bi-person-plus me-2"></i>
                              Ajouter un contact
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="cm-pagination">
                <div className="cm-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedContacts.length > 0 && (
                    <span className="cm-pagination-selected">
                      • <strong>{selectedContacts.length}</strong> sélectionné{selectedContacts.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="cm-pagination-controls">
                  <button
                    className="cm-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button
                    className="cm-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`cm-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="cm-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button
                    className="cm-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================
          MODAL AJOUT CONTACT
          ============================================ */}
      <Modal show={showAddContactModal} onHide={() => setShowAddContactModal(false)} size="lg" centered className="cm-modal">
        <div className="cm-modal-header cm-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="cm-modal-icon cm-modal-icon-success">
              <i className="bi bi-person-plus-fill"></i>
            </div>
            <div>
              <h5 className="cm-modal-title mb-0">Nouveau contact</h5>
              <small className="cm-modal-subtitle">Ajouter un contact à votre carnet</small>
            </div>
          </div>
          <button className="cm-modal-close" onClick={() => setShowAddContactModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="cm-modal-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">
                  Nom <span className="cm-required">*</span>
                </label>
                <input
                  type="text"
                  className="cm-input"
                  value={newContact.nom}
                  onChange={(e) => setNewContact({ ...newContact, nom: e.target.value })}
                  placeholder="Entrez le nom"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">
                  Prénom <span className="cm-required">*</span>
                </label>
                <input
                  type="text"
                  className="cm-input"
                  value={newContact.prenom}
                  onChange={(e) => setNewContact({ ...newContact, prenom: e.target.value })}
                  placeholder="Entrez le prénom"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">
                  Téléphone <span className="cm-required">*</span>
                </label>
                <input
                  type="tel"
                  className={`cm-input ${newContact.telephone && !isValidInternationalPhone(newContact.telephone) ? 'cm-input-error' : ''}`}
                  value={newContact.telephone}
                  onChange={(e) => setNewContact({ ...newContact, telephone: e.target.value.replace(/[^\d+\s-]/g, '') })}
                  placeholder="+241XXXXXXXX"
                />
                {newContact.telephone && !isValidInternationalPhone(newContact.telephone) ? (
                  <div className="cm-form-error">
                    <i className="bi bi-exclamation-circle"></i>
                    Format invalide. Utilisez +241 suivi de 8 chiffres.
                  </div>
                ) : (
                  <div className="cm-form-hint">
                    <i className="bi bi-info-circle"></i>
                    Format: +241 suivi de 8 chiffres
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">Email</label>
                <input
                  type="email"
                  className="cm-input"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="exemple@email.com"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">Entreprise</label>
                <input
                  type="text"
                  className="cm-input"
                  value={newContact.entreprise}
                  onChange={(e) => setNewContact({ ...newContact, entreprise: e.target.value })}
                  placeholder="Nom de l'entreprise"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="cm-form-group">
                <label className="cm-form-label">Fonction</label>
                <input
                  type="text"
                  className="cm-input"
                  value={newContact.fonction}
                  onChange={(e) => setNewContact({ ...newContact, fonction: e.target.value })}
                  placeholder="Poste occupé"
                />
              </div>
            </Col>
            <Col md={12}>
              <div className="cm-form-group">
                <label className="cm-form-label">Groupes</label>
                <Select
                  isMulti
                  value={contactGroups
                    .filter(group => newContact.groupes.includes(group.id))
                    .map(group => ({ value: group.id, label: group.name }))}
                  options={contactGroups.map(group => ({ value: group.id, label: group.name }))}
                  onChange={(selected) => {
                    const ids = selected ? selected.map(o => o.value) : [];
                    setNewContact(prev => ({ ...prev, groupes: ids }));
                  }}
                  placeholder="Sélectionnez un ou plusieurs groupes..."
                  isDisabled={contactGroups.length === 0}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '10px',
                      borderColor: '#e2e8f0',
                      minHeight: '44px',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#10b981' }
                    }),
                    multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
                    multiValueLabel: (base) => ({ ...base, color: '#059669', fontWeight: 600 }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#059669',
                      ':hover': { backgroundColor: '#059669', color: 'white' }
                    })
                  }}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <div className="cm-modal-footer">
          <button className="cm-btn-cancel" onClick={() => setShowAddContactModal(false)}>
            Annuler
          </button>
          <button
            className="cm-btn-primary"
            onClick={handleSaveContact}
            disabled={createLoading || !newContact.nom.trim() || !newContact.prenom.trim() || !newContact.telephone.trim() || !isValidInternationalPhone(newContact.telephone)}
          >
            {createLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Création...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Enregistrer
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL ÉDITION CONTACT
          ============================================ */}
      <Modal show={showEditContactModal} onHide={() => setShowEditContactModal(false)} size="lg" centered className="cm-modal">
        <div className="cm-modal-header cm-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="cm-modal-icon cm-modal-icon-blue">
              <i className="bi bi-pencil-square"></i>
            </div>
            <div>
              <h5 className="cm-modal-title mb-0">Modifier le contact</h5>
              <small className="cm-modal-subtitle">{selectedContact?.prenom} {selectedContact?.nom}</small>
            </div>
          </div>
          <button className="cm-modal-close" onClick={() => setShowEditContactModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="cm-modal-body">
          {selectedContact && (
            <Row className="g-3">
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">
                    Nom <span className="cm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="cm-input"
                    value={editContact.nom}
                    onChange={(e) => setEditContact({ ...editContact, nom: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">
                    Prénom <span className="cm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="cm-input"
                    value={editContact.prenom}
                    onChange={(e) => setEditContact({ ...editContact, prenom: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">
                    Téléphone <span className="cm-required">*</span>
                  </label>
                  <input
                    type="tel"
                    className="cm-input"
                    value={editContact.telephone}
                    onChange={(e) => setEditContact({ ...editContact, telephone: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">Email</label>
                  <input
                    type="email"
                    className="cm-input"
                    value={editContact.email}
                    onChange={(e) => setEditContact({ ...editContact, email: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">Entreprise</label>
                  <input
                    type="text"
                    className="cm-input"
                    value={editContact.entreprise}
                    onChange={(e) => setEditContact({ ...editContact, entreprise: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-form-label">Fonction</label>
                  <input
                    type="text"
                    className="cm-input"
                    value={editContact.fonction}
                    onChange={(e) => setEditContact({ ...editContact, fonction: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className="cm-form-group">
                  <label className="cm-form-label">Groupes</label>
                  <Select
                    isMulti
                    value={contactGroups
                      .filter(group => editContact.groupes.includes(group.id))
                      .map(group => ({ value: group.id, label: group.name }))}
                    options={contactGroups.map(group => ({ value: group.id, label: group.name }))}
                    onChange={(selected) => {
                      const ids = selected ? selected.map(o => o.value) : [];
                      setEditContact(prev => ({ ...prev, groupes: ids }));
                    }}
                    placeholder="Sélectionnez un ou plusieurs groupes..."
                    isDisabled={contactGroups.length === 0}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: '10px',
                        borderColor: '#e2e8f0',
                        minHeight: '44px',
                        boxShadow: 'none',
                        '&:hover': { borderColor: '#3b82f6' }
                      }),
                      multiValue: (base) => ({ ...base, backgroundColor: '#eff6ff' }),
                      multiValueLabel: (base) => ({ ...base, color: '#3b82f6', fontWeight: 600 }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: '#3b82f6',
                        ':hover': { backgroundColor: '#3b82f6', color: 'white' }
                      })
                    }}
                  />
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <div className="cm-modal-footer">
          <button className="cm-btn-cancel" onClick={() => setShowEditContactModal(false)}>
            Annuler
          </button>
          <button
            className="cm-btn-primary"
            onClick={handleUpdateContact}
            disabled={updateLoading || !editContact.nom.trim() || !editContact.prenom.trim() || !editContact.telephone.trim()}
          >
            {updateLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Mise à jour...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Mettre à jour
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL DÉTAILS
          ============================================ */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="cm-modal">
        <div className="cm-modal-header cm-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="cm-modal-icon cm-modal-icon-info">
              <i className="bi bi-person-badge"></i>
            </div>
            <div>
              <h5 className="cm-modal-title mb-0">Détails du contact</h5>
              <small className="cm-modal-subtitle">Informations complètes</small>
            </div>
          </div>
          <button className="cm-modal-close" onClick={() => setShowDetailsModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="cm-modal-body">
          {selectedContact && (
            <>
              {/* HERO */}
              <div className="cm-detail-hero">
                <div className="cm-detail-avatar">
                  {selectedContact.nom?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="cm-detail-hero-content">
                  <div className="cm-detail-hero-name">
                    {selectedContact.prenom} {selectedContact.nom}
                  </div>
                  <div className="cm-detail-hero-meta">
                    <span>
                      <i className="bi bi-telephone"></i>
                      {selectedContact.telephone}
                    </span>
                    {selectedContact.email && (
                      <span>
                        <i className="bi bi-envelope"></i>
                        {selectedContact.email}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`cm-status-badge ${selectedContact.actif === 1 ? 'cm-status-active' : 'cm-status-inactive'}`}>
                  <i className={`bi ${selectedContact.actif === 1 ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                  {selectedContact.actif === 1 ? "Actif" : "Inactif"}
                </span>
              </div>

              {/* GRID */}
              <div className="cm-detail-grid">
                <div className="cm-detail-card">
                  <div className="cm-detail-card-icon cm-detail-card-icon-blue">
                    <i className="bi bi-building"></i>
                  </div>
                  <div className="cm-detail-card-content">
                    <div className="cm-detail-card-label">Entreprise</div>
                    <div className="cm-detail-card-value">{selectedContact.entreprise || '—'}</div>
                  </div>
                </div>

                <div className="cm-detail-card">
                  <div className="cm-detail-card-icon cm-detail-card-icon-violet">
                    <i className="bi bi-briefcase"></i>
                  </div>
                  <div className="cm-detail-card-content">
                    <div className="cm-detail-card-label">Fonction</div>
                    <div className="cm-detail-card-value">{selectedContact.fonction || '—'}</div>
                  </div>
                </div>

                <div className="cm-detail-card">
                  <div className="cm-detail-card-icon cm-detail-card-icon-amber">
                    <i className="bi bi-calendar"></i>
                  </div>
                  <div className="cm-detail-card-content">
                    <div className="cm-detail-card-label">Créé le</div>
                    <div className="cm-detail-card-value">
                      {selectedContact.created_at ? new Date(selectedContact.created_at).toLocaleDateString('fr-FR') : '—'}
                    </div>
                  </div>
                </div>

                <div className="cm-detail-card">
                  <div className="cm-detail-card-icon cm-detail-card-icon-green">
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div className="cm-detail-card-content">
                    <div className="cm-detail-card-label">Modifié le</div>
                    <div className="cm-detail-card-value">
                      {selectedContact.updated_at ? new Date(selectedContact.updated_at).toLocaleDateString('fr-FR') : '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* GROUPES */}
              <div className="cm-detail-section">
                <div className="cm-detail-section-header">
                  <i className="bi bi-collection"></i>
                  <span>Groupes</span>
                </div>
                <div className="cm-detail-section-body">
                  {renderGroupBadges(selectedContact.groupes)}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <div className="cm-modal-footer">
          <button className="cm-btn-cancel" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </button>
          {selectedContact && (
            <button
              className="cm-btn-primary"
              onClick={() => {
                setShowDetailsModal(false);
                handleEditContact(selectedContact);
              }}
            >
              <i className="bi bi-pencil"></i>
              Modifier
            </button>
          )}
        </div>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION
          ============================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="cm-modal">
        <Modal.Body className="cm-delete-body">
          <div className="cm-delete-icon-wrapper">
            <div className="cm-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="cm-delete-title">Confirmer la suppression</h4>
          <p className="cm-delete-text">Êtes-vous sûr de vouloir supprimer ce contact ?</p>

          {contactToDelete && (
            <div className="cm-delete-contact">
              <div className="cm-delete-avatar">
                {contactToDelete.nom?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="cm-delete-info">
                <div className="cm-delete-name">
                  {contactToDelete.prenom} {contactToDelete.nom}
                </div>
                <div className="cm-delete-meta">
                  <code>{contactToDelete.telephone}</code>
                  {contactToDelete.email && (
                    <span>{contactToDelete.email}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="cm-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="cm-delete-actions">
            <button
              className="cm-btn-cancel cm-btn-flex"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button
              className="cm-btn-danger cm-btn-flex"
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION MULTIPLE
          ============================================ */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered className="cm-modal">
        <Modal.Body className="cm-delete-body">
          <div className="cm-delete-icon-wrapper">
            <div className="cm-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="cm-delete-title">Suppression multiple</h4>
          <p className="cm-delete-text">
            Vous êtes sur le point de supprimer <strong>{selectedContacts.length} contact(s)</strong>.
          </p>

          <div className="cm-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="cm-delete-actions">
            <button
              className="cm-btn-cancel cm-btn-flex"
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={bulkDeleteLoading}
            >
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button
              className="cm-btn-danger cm-btn-flex"
              onClick={handleConfirmBulkDelete}
              disabled={bulkDeleteLoading}
            >
              {bulkDeleteLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer ({selectedContacts.length})
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ============================================
          STYLES PREMIUM
          ============================================ */}
      <style>{`
        /* ========== GLOBAL ========== */
        .cm-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .cm-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .cm-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .cm-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .cm-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .cm-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .cm-btn-primary, .cm-btn-secondary, .cm-btn-cancel, .cm-btn-danger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .cm-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .cm-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .cm-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .cm-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .cm-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .cm-btn-secondary:disabled {
          opacity: 0.6;
        }
        .cm-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .cm-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .cm-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .cm-btn-cancel:hover {
          background: #f1f5f9;
        }
        .cm-btn-flex {
          flex: 1;
        }
        .cm-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
        }
        .cm-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
        }
        .cm-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== ALERTS ========== */
        .cm-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .cm-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .cm-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .cm-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .cm-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .cm-alert-info {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border-left: 4px solid #3b82f6;
        }
        .cm-alert-close {
          background: none;
          border: none;
          color: inherit;
          opacity: 0.6;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 1rem;
        }
        .cm-alert-close:hover {
          opacity: 1;
          background: rgba(0,0,0,0.05);
        }

        /* ========== STATS ========== */
        .cm-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .cm-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .cm-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .cm-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .cm-stat-icon-green { background: #d1fae5; color: #10b981; }
        .cm-stat-icon-gray  { background: #f1f5f9; color: #64748b; }
        .cm-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .cm-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .cm-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .cm-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .cm-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .cm-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .cm-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .cm-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .cm-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .cm-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .cm-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .cm-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .cm-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .cm-table {
          font-size: 0.875rem;
        }
        .cm-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .cm-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .cm-row {
          transition: background-color 0.15s;
        }
        .cm-row:hover {
          background-color: #f8fafc;
        }
        .cm-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .cm-sortable-header:hover {
          color: #3b82f6;
        }
        .cm-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .cm-sort-icon {
          color: #3b82f6;
          font-size: 0.75rem;
        }

        /* Checkbox */
        .cm-checkbox-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cm-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #cbd5e1;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        /* Name cell */
        .cm-name-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cm-avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
        }
        .cm-name-main {
          font-weight: 700;
          color: #0f172a;
        }
        .cm-prenom {
          color: #334155;
          font-weight: 500;
        }
        .cm-fonction {
          color: #64748b;
          font-size: 0.82rem;
        }
        .cm-date-cell {
          color: #64748b;
          font-size: 0.8rem;
          font-variant-numeric: tabular-nums;
        }

        .cm-code {
          font-size: 0.78rem;
          color: #1e40af;
          background: #eff6ff;
          padding: 4px 8px;
          border-radius: 5px;
          font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        /* Badges */
        .cm-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .cm-badge-secondary {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .cm-group-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .cm-group-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 5px;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .cm-group-badge-blue   { background: #eff6ff; color: #3b82f6; }
        .cm-group-badge-green  { background: #d1fae5; color: #059669; }
        .cm-group-badge-red    { background: #fee2e2; color: #ef4444; }
        .cm-group-badge-amber  { background: #fef3c7; color: #d97706; }
        .cm-group-badge-cyan   { background: #cffafe; color: #0891b2; }
        .cm-group-badge-violet { background: #ede9fe; color: #7c3aed; }
        .cm-group-badge-gray   { background: #f1f5f9; color: #64748b; }

        .cm-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 11px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.72rem;
          white-space: nowrap;
        }
        .cm-status-active {
          background: #d1fae5;
          color: #059669;
        }
        .cm-status-inactive {
          background: #f1f5f9;
          color: #64748b;
        }

        /* Actions */
        .cm-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .cm-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.9rem;
        }
        .cm-action-view:hover {
          background: #06b6d4;
          border-color: #06b6d4;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .cm-action-edit:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }
        .cm-action-delete:hover {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .cm-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .cm-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: cm-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .cm-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .cm-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .cm-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .cm-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .cm-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .cm-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .cm-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .cm-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .cm-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cm-pagination-info strong {
          color: #0f172a;
        }
        .cm-pagination-selected {
          color: #3b82f6;
          margin-left: 8px;
        }
        .cm-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cm-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .cm-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .cm-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .cm-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== MODAL ========== */
        .cm-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .cm-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .cm-modal-header-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .cm-modal-header-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .cm-modal-header-info {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        }
        .cm-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .cm-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .cm-modal-subtitle {
          color: rgba(255,255,255,0.85);
          font-size: 0.8rem;
        }
        .cm-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
        }
        .cm-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .cm-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .cm-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Form */
        .cm-form-group {
          margin-bottom: 4px;
        }
        .cm-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.82rem;
          margin-bottom: 8px;
        }
        .cm-required {
          color: #ef4444;
        }
        .cm-input {
          width: 100%;
          padding: 11px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .cm-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .cm-input-error {
          border-color: #ef4444;
        }
        .cm-input-error:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
        }
        .cm-form-hint {
          margin-top: 6px;
          font-size: 0.72rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .cm-form-hint i {
          color: #3b82f6;
        }
        .cm-form-error {
          margin-top: 6px;
          font-size: 0.72rem;
          color: #ef4444;
          display: flex;
          align-items: center;
          gap: 4px;
          font-weight: 600;
        }

        /* Detail Hero */
        .cm-detail-hero {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .cm-detail-avatar {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 24px;
          flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.3);
        }
        .cm-detail-hero-content {
          flex-grow: 1;
          min-width: 0;
        }
        .cm-detail-hero-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.15rem;
          margin-bottom: 6px;
        }
        .cm-detail-hero-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          font-size: 0.82rem;
          color: #64748b;
        }
        .cm-detail-hero-meta span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .cm-detail-hero-meta i {
          color: #94a3b8;
        }

        /* Detail Grid */
        .cm-detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .cm-detail-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .cm-detail-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08);
        }
        .cm-detail-card-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .cm-detail-card-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .cm-detail-card-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .cm-detail-card-icon-amber  { background: #fef3c7; color: #d97706; }
        .cm-detail-card-icon-green  { background: #d1fae5; color: #059669; }
        .cm-detail-card-content {
          flex-grow: 1;
          min-width: 0;
        }
        .cm-detail-card-label {
          font-size: 0.68rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .cm-detail-card-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
        }

        /* Detail Section */
        .cm-detail-section {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .cm-detail-section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 18px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
        }
        .cm-detail-section-header i {
          color: #3b82f6;
          font-size: 1rem;
        }
        .cm-detail-section-body {
          padding: 16px 18px;
        }

        /* Delete modal */
        .cm-delete-body {
          padding: 32px 24px 24px;
          text-align: center;
        }
        .cm-delete-icon-wrapper {
          margin-bottom: 20px;
        }
        .cm-delete-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .cm-delete-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          font-size: 1.25rem;
        }
        .cm-delete-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .cm-delete-contact {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
          text-align: left;
        }
        .cm-delete-avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 18px;
          flex-shrink: 0;
        }
        .cm-delete-info {
          min-width: 0;
          flex-grow: 1;
        }
        .cm-delete-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }
        .cm-delete-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          font-size: 0.78rem;
          color: #94a3b8;
        }
        .cm-delete-warning {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          margin-bottom: 20px;
        }
        .cm-delete-actions {
          display: flex;
          gap: 10px;
        }

        /* ========== ANIMATIONS ========== */
        .cm-spin {
          animation: cm-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes cm-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .cm-detail-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .cm-header {
            padding: 20px;
          }
          .cm-header-title {
            font-size: 1.2rem;
          }
          .cm-pagination {
            flex-direction: column;
          }
          .cm-stat-value {
            font-size: 1.25rem;
          }
          .cm-modal-body {
            padding: 16px;
          }
          .cm-delete-actions {
            flex-direction: column;
          }
          .cm-detail-hero {
            padding: 16px;
          }
          .cm-detail-avatar {
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
}